import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import './error.css';
import { Link } from "react-router-dom";
import Loader from '../components/layout/preloader';
import { supabase } from '../supabaseClient';

gsap.registerPlugin();

const Error = () => {
  const PRELOADER = 4.1;

  const [errorData, setErrorData] = useState([]);
  const [dataReady, setDataReady] = useState(false);

  const asteriskRef = useRef();
  const codeRef = useRef();
  const btnRef = useRef();
  const tagRef = useRef();
  const wrapperRef = useRef();

  useEffect(() => {
    const getErrorData = async () => {
      const res = await supabase.from('error_404').select('*');
      setErrorData(res.data || []);
      setDataReady(true);
    };
    getErrorData();
  }, []);

  useEffect(() => {
    if (!dataReady) return;

    const ctx = gsap.context(() => {

      gsap.fromTo(wrapperRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.5, ease: 'power2.out', delay: PRELOADER }
      );

      gsap.fromTo(asteriskRef.current,
        { x: -200, y: -200, rotation: -360, opacity: 0, scale: 0.3 },
        {
          x: 0,
          y: 0,
          rotation: 0,
          opacity: 1,
          scale: 1,
          duration: 1.4,
          ease: 'elastic.out(1, 0.45)',
          delay: PRELOADER + 0.1
        }
      );

      gsap.to(asteriskRef.current, {
        rotation: 360,
        duration: 8,
        repeat: -1,
        ease: 'none',
        delay: PRELOADER + 1.6
      });

      gsap.to(asteriskRef.current, {
        y: -18,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: PRELOADER + 1.8
      });

      gsap.fromTo(codeRef.current,
        { y: -80, opacity: 0, skewX: -10 },
        { y: 0, opacity: 1, skewX: 0, duration: 0.9, ease: 'expo.out', delay: PRELOADER + 0.4 }
      );

      const glitchTl = gsap.timeline({ delay: PRELOADER + 1.5, repeat: -1, repeatDelay: 4 });
      glitchTl
        .to(codeRef.current, { skewX: 8, x: 6, duration: 0.05 })
        .to(codeRef.current, { skewX: -6, x: -4, duration: 0.05 })
        .to(codeRef.current, { skewX: 4, x: 3, duration: 0.05 })
        .to(codeRef.current, { skewX: 0, x: 0, duration: 0.05 })
        .to(codeRef.current, { opacity: 0.3, duration: 0.04 })
        .to(codeRef.current, { opacity: 1, duration: 0.04 })
        .to(codeRef.current, { skewX: -3, x: -2, duration: 0.04 })
        .to(codeRef.current, { skewX: 0, x: 0, duration: 0.04 });

      gsap.fromTo(btnRef.current,
        { scale: 0, opacity: 0, rotation: -20 },
        {
          scale: 1,
          opacity: 1,
          rotation: 0,
          duration: 0.9,
          ease: 'elastic.out(1, 0.4)',
          delay: PRELOADER + 0.7
        }
      );

      const btn = btnRef.current?.querySelector('.error-btn');
      if (btn) {
        btn.addEventListener('mouseenter', () => {
          gsap.to(btn, { scale: 1.07, duration: 0.3, ease: 'back.out(2)' });
        });
        btn.addEventListener('mouseleave', () => {
          gsap.to(btn, { scale: 1, duration: 0.4, ease: 'elastic.out(1, 0.4)' });
        });
      }

      gsap.fromTo(tagRef.current,
        { y: 60, opacity: 0, letterSpacing: '18px' },
        {
          y: 0,
          opacity: 1,
          letterSpacing: '3.364px',
          duration: 1,
          ease: 'expo.out',
          delay: PRELOADER + 0.9
        }
      );

      const handleMouseMove = (e) => {
        if (!asteriskRef.current) return;
        const rect = asteriskRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = (e.clientX - cx) * 0.12;
        const dy = (e.clientY - cy) * 0.12;

        gsap.to(asteriskRef.current, {
          x: dx,
          y: dy,
          duration: 0.5,
          ease: 'power2.out',
          overwrite: 'auto'
        });
      };

      const handleMouseLeave = () => {
        gsap.to(asteriskRef.current, {
          x: 0,
          y: 0,
          duration: 1,
          ease: 'elastic.out(1, 0.3)',
          overwrite: 'auto'
        });
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseleave', handleMouseLeave);
      };

    });

    return () => ctx.revert();
  }, [dataReady]);

  return (
    <>
      <Loader />

      <main className="error-page">
        <div className="error-wrapper gsap-hidden" ref={wrapperRef}>

          {errorData.map((item) => (
            <React.Fragment key={item.error_code}>
              <div className="error-image-col">
                <img
                  src={item['as-img']}
                  alt=""
                  aria-hidden="true"
                  className="error-asterisk"
                  ref={asteriskRef}
                  style={{ animation: 'none' }}
                />
              </div>

              <div className="error-text-col">
                <div className="error-top-row">
                  <h1 className="error-code" ref={codeRef}>
                    {item.error_code}
                  </h1>

                  <div ref={btnRef}>
                    <Link to="/" className="no-underline-link">
                      <button className="error-btn" type="button">
                        <svg className="error-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="19" y1="12" x2="5" y2="12" />
                          <polyline points="12 19 5 12 12 5" />
                        </svg>
                        {item.button_text_en}
                      </button>
                    </Link>
                  </div>
                </div>

                <div className="error-bottom-row">
                  <p className="error-tag" ref={tagRef}>
                    {item.title_en}
                  </p>
                </div>
              </div>
            </React.Fragment>
          ))}

        </div>
      </main>

    </>
  );
};

export default Error;